import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useMatch, useNavigate } from '@tanstack/react-router';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectMemoStore } from '@/entities/project-memo/model/store';
import { useReleaseFlag } from '@/entities/session/model/useReleaseFlag';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Button, Tooltip } from '@/shared/ui';
import { SideMenuHeader } from '@/widgets/right-side-menu/ui/SideMenuHeader';

type Props = {
  projectId: string;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  /** 現在表示中のスレッドID。スレッド未選択（welcome 画面）の場合は undefined */
  threadId: string | undefined;
};

export const ProjectDetailAIChatHeader: FC<Props> = ({
  projectId,
  showHistory,
  setShowHistory,
  threadId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 現在ページが事業詳細・メモのどちらか判定する。閉じる/遷移先 URL を現在ページに合わせる。
  const isOnMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });

  const requestClose = useRouteSidePanelStore((state) => state.requestClose);
  const setPendingThreadId = useProjectMemoStore((state) => state.setPendingThreadId);
  const isMemoEnabled = useReleaseFlag('memo');

  const handleClose = () => {
    requestClose();
    // 現在のページに留まり、URL の threadId のみ削除する（メモページでは memoId を保持）
    if (isOnMemoPage) {
      navigate({
        to: '/projects/$projectId/memo',
        params: { projectId },
        search: { threadId: undefined, memoId: isOnMemoPage.search.memoId },
      });
    } else {
      navigate({
        to: '/projects/$projectId',
        params: { projectId },
        search: { threadId: undefined },
      });
    }
  };

  const handleCreateMemoFromThread = () => {
    if (!threadId) return;
    // メモページ側で creating モードに自動遷移させるための共有ストア
    setPendingThreadId(threadId);
    // 既にメモページにいる場合でも threadId クエリは維持して AI チャットを継続表示する
    navigate({
      to: '/projects/$projectId/memo',
      params: { projectId },
      search: { threadId },
    });
  };

  const title = t('ai.chat.title');

  // 履歴表示中: 戻るボタン
  if (showHistory) {
    return (
      <SideMenuHeader
        title={t('ai.chat-history.title')}
        onClose={handleClose}
        onBack={() => setShowHistory(false)}
      />
    );
  }

  // デフォルト（チャット/ウェルカム画面）
  return (
    <SideMenuHeader
      title={title}
      additionalMenuItems={
        <>
          {threadId && isMemoEnabled && (
            <Tooltip tooltipContent={t('ai.chat.create-memo-from-thread')}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleCreateMemoFromThread}
                aria-label={t('ai.chat.create-memo-from-thread')}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </Button>
            </Tooltip>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>
            {t('ai.chat.history-button')}
          </Button>
        </>
      }
      onClose={handleClose}
    />
  );
};
